import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';

import AppShell from './components/AppShell/AppShell';
import ProtectedRoute from './components/ProtectedRoute';
import W3LoginPage from './components/Auth/W3LoginPage';
import ChatPage from './pages/ChatPage';
import ProjectsIndexPage from './pages/ProjectsIndexPage';
import ProjectFilesPage from './pages/ProjectFilesPage';
import ReportsLayout from './components/Reports/ReportsLayout';
import ReportsCompaniesPage from './pages/ReportsCompaniesPage';
import ReportsCompanyPage from './pages/ReportsCompanyPage';
import ReportsReportPage from './pages/ReportsReportPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<W3LoginPage />} />

            <Route
              element={
                <ProtectedRoute>
                  <ReportsLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/reports" element={<ReportsCompaniesPage />} />
              <Route path="/reports/companies/:companyId" element={<ReportsCompanyPage />} />
              <Route path="/reports/:reportId" element={<ReportsReportPage />} />
            </Route>

            <Route
              element={
                <ProtectedRoute>
                  <AppShell />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/c/new" replace />} />
              <Route path="/c/new" element={<ChatPage />} />
              <Route path="/c/:chatId" element={<ChatPage />} />
              <Route path="/p" element={<ProjectsIndexPage />} />
              {/* Project landing → straight into a project-scoped chat.
                  Familiar Claude-style flow: pick a project = start chatting.
                  Bookmarks of /p/:projectId still work. */}
              <Route path="/p/:projectId" element={<Navigate to="c/new" replace />} />
              <Route path="/p/:projectId/files" element={<ProjectFilesPage />} />
              <Route path="/p/:projectId/c/new" element={<ChatPage />} />
              <Route path="/p/:projectId/c/:chatId" element={<ChatPage />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
      </ThemeProvider>

      {process.env.NODE_ENV === 'development' && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}
    </QueryClientProvider>
  );
}

export default App;
